import { useCallback, useMemo } from "react";
import { useFormik } from "formik";
import _ from "lodash";

import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Autocomplete from "components/Autocomplete";
import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";

import { useDealTablesAvailableSheetList } from "entities/Deal/sdk";
import {
  COLUMN_OPTIONS,
  generateSheetOptions,
  ROW_OPTIONS,
} from "entities/TableStorage/components/TableImportCreateDialogButton/utils";
import { tableImportUpdate } from "entities/TableStorage/sdk";

import { VALIDATION_SCHEMA } from "./constants";

export interface IFormValues {
  sheet_name: string;
  column_start: string;
  column_end: string;
  row_start: number;
  row_end: number;
}

export interface ITableImportUpdateDialog {
  show: boolean;
  onClose: () => void;
  tableImportId: number;
  initialValues: IFormValues;
  updateCallback: () => void;
  dealId: number;
}

const TableImportUpdateDialog = ({
  show,
  onClose,
  tableImportId,
  initialValues,
  updateCallback,
  dealId,
}: ITableImportUpdateDialog) => {
  const { data: sheets } = useDealTablesAvailableSheetList({
    dealId,
    enabled: show,
  });

  const sheetOptions = useMemo(
    () => generateSheetOptions({ sheets }),
    [sheets]
  );

  const formik = useFormik<IFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (values) =>
      tableImportUpdate({ values, tableImportId })
        .then(() => {
          toast.successMessage("Table import modified successfully");
          close();
          updateCallback();
        })
        .catch(handleInvalidRequest),
  });

  const close = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  return (
    <Dialog fullWidth maxWidth="lg" open={show} onClose={close}>
      <DialogTitle
        sx={{
          margin: 2,
          borderRadius: "8px",
          backgroundColor: colors.blue20,
        }}
      >
        <Text variant="h2" marginBottom={0.5} color={colors.gray100}>
          Modify cell range of synced table
        </Text>
      </DialogTitle>

      <DialogContent sx={{ padding: 2, overflowY: "visible" }}>
        <Text variant="text3" fontWeight={500}>
          Cell range
        </Text>
        <Text variant="text4" color={colors.gray60} marginBottom={2}>
          Select the sheet and cell range that will get synced.
        </Text>

        <Stack direction="row" spacing={2}>
          <Stack sx={{ flexBasis: "35%" }}>
            <Text variant="text3" fontWeight={500}>
              Sheet
            </Text>
            <Autocomplete
              {...formik.getFieldProps("sheet_name")}
              disableClearable
              options={sheetOptions}
              size="medium"
              selectOnFocus
              onChange={(event, value) => {
                if (!_.isNil(value)) {
                  formik.setFieldValue("sheet_name", value.value);
                }
              }}
              renderInput={(params) => <TextField {...params} label="" />}
              sx={{ background: colors.gray10 }}
            />
          </Stack>
          <Stack sx={{ flexBasis: "15%" }}>
            <Text variant="text3" fontWeight={500}>
              Column: START
            </Text>
            <Autocomplete
              {...formik.getFieldProps("column_start")}
              disableClearable
              options={COLUMN_OPTIONS}
              size="medium"
              selectOnFocus
              onChange={(event, value) => {
                if (!_.isNil(value)) {
                  formik.setFieldValue("column_start", value);
                }
              }}
              renderInput={(params) => <TextField {...params} label="" />}
              sx={{ background: colors.gray10 }}
            />
          </Stack>
          <Stack sx={{ flexBasis: "15%" }}>
            <Text variant="text3" fontWeight={500}>
              Column: END
            </Text>
            <Autocomplete
              {...formik.getFieldProps("column_end")}
              disableClearable
              options={COLUMN_OPTIONS}
              size="medium"
              selectOnFocus
              onChange={(event, value) => {
                if (!_.isNil(value)) {
                  formik.setFieldValue("column_end", value);
                }
              }}
              renderInput={(params) => <TextField {...params} label="" />}
              sx={{ background: colors.gray10 }}
            />
          </Stack>
          <Stack sx={{ flexBasis: "15%" }}>
            <Text variant="text3" fontWeight={500}>
              Row: START
            </Text>
            <Autocomplete
              {...formik.getFieldProps("row_start")}
              disableClearable
              options={ROW_OPTIONS}
              size="medium"
              selectOnFocus
              onChange={(event, value) => {
                if (!_.isNil(value)) {
                  formik.setFieldValue("row_start", value);
                }
              }}
              getOptionLabel={(option) => {
                return _.isNumber(option) ? option.toString() : option;
              }}
              renderInput={(params) => <TextField {...params} label="" />}
              sx={{ background: colors.gray10 }}
            />
          </Stack>
          <Stack sx={{ flexBasis: "15%" }}>
            <Text variant="text3" fontWeight={500}>
              Row: END
            </Text>
            <Autocomplete
              {...formik.getFieldProps("row_end")}
              disableClearable
              options={ROW_OPTIONS}
              size="medium"
              selectOnFocus
              onChange={(event, value) => {
                if (!_.isNil(value)) {
                  formik.setFieldValue("row_end", value);
                }
              }}
              getOptionLabel={(option) => {
                return _.isNumber(option) ? option.toString() : option;
              }}
              renderInput={(params) => <TextField {...params} label="" />}
              sx={{ background: colors.gray10 }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button variant="text" onClick={close}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={formik.submitForm}
          disabled={formik.isSubmitting}
        >
          Modify table import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TableImportUpdateDialog;
